type AuthSyncEvent = 'logout'
type AuthSyncPayload = {
  type: AuthSyncEvent
  sourceId: string
  timestamp: number
}

const AUTH_SYNC_CHANNEL = 'epic-auth-sync'
const AUTH_SYNC_STORAGE_KEY = 'epic-auth-sync-event'
const tabSourceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`

const listeners = new Set<(event: AuthSyncEvent) => void>()

let isInitialized = false
let channel: BroadcastChannel | null = null

function dispatchEvent(event: AuthSyncEvent) {
  listeners.forEach((listener) => {
    listener(event)
  })
}

function handlePayload(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return
  }

  const { type, sourceId } = payload as Partial<AuthSyncPayload>
  if (sourceId === tabSourceId) {
    return
  }
  if (type !== 'logout') {
    return
  }

  dispatchEvent(type)
}

function onStorage(event: StorageEvent) {
  if (event.key !== AUTH_SYNC_STORAGE_KEY || !event.newValue) {
    return
  }

  try {
    const payload = JSON.parse(event.newValue) as AuthSyncPayload
    handlePayload(payload)
  } catch {
    // Ignore malformed auth-sync payloads.
  }
}

function ensureInitialized() {
  if (isInitialized) {
    return
  }
  isInitialized = true

  if (typeof window === 'undefined') {
    return
  }

  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(AUTH_SYNC_CHANNEL)
    channel.onmessage = (message: MessageEvent<AuthSyncPayload>) => {
      handlePayload(message.data)
    }
  }

  window.addEventListener('storage', onStorage)
}

function createPayload(type: AuthSyncEvent): AuthSyncPayload {
  return {
    type,
    sourceId: tabSourceId,
    timestamp: Date.now(),
  }
}

export function broadcastAuthEvent(type: AuthSyncEvent) {
  ensureInitialized()

  if (typeof window === 'undefined') {
    return
  }

  const payload = createPayload(type)

  if (channel) {
    channel.postMessage(payload)
  }

  try {
    window.localStorage.setItem(AUTH_SYNC_STORAGE_KEY, JSON.stringify(payload))
    window.localStorage.removeItem(AUTH_SYNC_STORAGE_KEY)
  } catch {
    // Ignore storage write errors (private mode, quota, disabled storage).
  }
}

export function subscribeToAuthEvents(listener: (event: AuthSyncEvent) => void) {
  ensureInitialized()
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}
